import { BarChart3 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { MoneyText } from '@/components/ui/MoneyText'
import { Skeleton, SkeletonRows } from '@/components/ui/Skeleton'
import { StatTile } from '@/components/ui/StatTile'
import { Table, TBody, Td, THead, Th, Tr } from '@/components/ui/Table'
import type { DepartmentMonthSummary } from '@/data/reports'
import { DepartmentBarsChart } from '@/features/home/LazyCharts'
import { useMediaQuery } from '@/layouts/useMediaQuery'
import { formatKwacha } from '@/lib/money'
import { Link } from '@tanstack/react-router'
import { Versus } from './reportParts'

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')

/**
 * Every department side by side for one month. Like the quarterly summary, minus what only exists per
 * quarter (budgets, the running cash balance): in their place, the net change in each department's cash.
 */
export function ChurchMonthView({
  rows,
  before,
  loading,
  error,
  retry,
  footer,
}: {
  rows: DepartmentMonthSummary[] | undefined
  /** The month before, for the comparison lines. Left out quietly if it could not be loaded. */
  before: DepartmentMonthSummary[] | undefined
  loading: boolean
  error: unknown
  retry: () => void
  /** Printed at the foot of every page: who, which month, when. */
  footer: string
}) {
  const wide = useMediaQuery('(min-width: 1024px)')

  if (loading) {
    return (
      <div role="status" aria-label="Loading" className="flex flex-col gap-6">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 rounded-card" />
          ))}
        </div>
        <Card>
          <CardBody>
            <SkeletonRows rows={5} />
          </CardBody>
        </Card>
      </div>
    )
  }
  if (error) {
    return (
      <div>
        <p role="alert" className="text-body text-bad">{errText(error)}</p>
        <Button variant="secondary" size="lg" className="mt-3" onClick={retry}>Try again</Button>
      </div>
    )
  }
  if (!rows || rows.length === 0) {
    return (
      <Card>
        <EmptyState icon={BarChart3} title="No departments yet" description="Departments appear here once they exist." />
      </Card>
    )
  }

  const sum = (f: (r: DepartmentMonthSummary) => number) => rows.reduce((s, r) => s + f(r), 0)
  const sumBefore = (f: (r: DepartmentMonthSummary) => number) => (before ? before.reduce((s, r) => s + f(r), 0) : undefined)
  const collected = sum((r) => r.collectedNgwee)
  const spent = sum((r) => r.spentNgwee)
  const unconfirmed = sum((r) => r.unconfirmedNgwee)
  const tiles = [
    { label: 'Collected', value: collected, previous: sumBefore((r) => r.collectedNgwee), goodWhen: 'up' as const },
    { label: 'Spent', value: spent, previous: sumBefore((r) => r.spentNgwee), goodWhen: 'none' as const, extra: spent > collected ? `${formatKwacha(spent - collected)} more than was collected` : undefined },
    { label: 'Sent to central', value: sum((r) => r.sentNgwee), previous: sumBefore((r) => r.sentNgwee), goodWhen: 'none' as const },
    { label: 'Sent but not confirmed', value: unconfirmed, previous: sumBefore((r) => r.unconfirmedNgwee), goodWhen: 'down' as const, warn: unconfirmed > 0 },
  ]

  return (
    <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-label="Church totals">
        {tiles.map((t) => (
          <StatTile
            key={t.label}
            label={t.label}
            tone={t.warn ? 'warning' : undefined}
            value={<MoneyText ngwee={t.value} />}
            detail={
              <span className="flex flex-col gap-0.5">
                <Versus current={t.value} previous={t.previous} goodWhen={t.goodWhen} since="last month" />
                {t.extra && <span className="text-wait">{t.extra}</span>}
              </span>
            }
          />
        ))}
      </div>

      <Card>
        <CardHeader title="Collected and spent, by department" />
        <CardBody>
          <DepartmentBarsChart wide={wide} data={rows.map((r) => ({ label: r.name, collectedNgwee: r.collectedNgwee, spentNgwee: r.spentNgwee }))} />
        </CardBody>
      </Card>

      <Card>
        <Table maxHeightClass="max-h-[70vh] print:max-h-none">
          <THead>
            <Tr className="border-b-0 hover:bg-transparent">
              <Th rowSpan={2} className="align-bottom">Department</Th>
              <Th colSpan={4} scope="colgroup" className="border-b border-line text-center">Activity this month</Th>
              <Th colSpan={2} scope="colgroup" className="border-b border-l border-line text-center">Position</Th>
            </Tr>
            <Tr className="hover:bg-transparent">
              <Th numeric>Collected</Th>
              <Th numeric>Spent</Th>
              <Th numeric>Sent</Th>
              <Th numeric>Confirmed</Th>
              <Th numeric className="border-l border-line">Net change in cash</Th>
              <Th numeric>Dues owed (now)</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((d) => (
              <Tr key={d.departmentId}>
                <Td>
                  <Link to="/d/$deptId/report" params={{ deptId: d.departmentId }} className="font-medium text-brand hover:underline">{d.name}</Link>
                </Td>
                <Td numeric><MoneyText ngwee={d.collectedNgwee} /></Td>
                <Td numeric><MoneyText ngwee={d.spentNgwee} /></Td>
                <Td numeric><MoneyText ngwee={d.sentNgwee} /></Td>
                <Td numeric>
                  <MoneyText ngwee={d.confirmedNgwee} />
                  {d.unconfirmedNgwee > 0 && <span className="block text-micro text-wait">{formatKwacha(d.unconfirmedNgwee)} not confirmed</span>}
                </Td>
                <Td numeric className="border-l border-line"><MoneyText ngwee={d.cashChangeNgwee} /></Td>
                <Td numeric><MoneyText ngwee={d.arrearsNgwee} /></Td>
              </Tr>
            ))}
            <Tr total>
              <Td>Total</Td>
              <Td numeric><MoneyText ngwee={collected} strongZero /></Td>
              <Td numeric><MoneyText ngwee={spent} strongZero /></Td>
              <Td numeric><MoneyText ngwee={sum((r) => r.sentNgwee)} strongZero /></Td>
              <Td numeric><MoneyText ngwee={sum((r) => r.confirmedNgwee)} strongZero /></Td>
              <Td numeric className="border-l border-line"><MoneyText ngwee={sum((r) => r.cashChangeNgwee)} strongZero /></Td>
              <Td numeric><MoneyText ngwee={sum((r) => r.arrearsNgwee)} strongZero /></Td>
            </Tr>
          </TBody>
        </Table>
      </Card>

      <p className="text-body text-muted no-print">
        Budgets and each department's running cash balance are kept per quarter: choose Quarterly to see them.
      </p>

      {/* Only on paper: who and when, repeated at the foot of every printed page. */}
      <p className="print-footer">{footer}</p>
    </>
  )
}
